import { Link as ReactLink } from 'react-router-dom';
import { css, styled } from 'styled-components';

import { theme } from '../../styles';
import { CircleInfoIcon, EditIcon, TriangleExclamationIcon } from '../../assets/icons';
import type { ResponsiveSpacing } from '../../utils';
import { getUrlType, useTabbing } from '../../utils';
import { Container, ResponsiveSpacer } from '../Layout';

const StyledAlert = styled(Container)<AlertProps>`
  ${props => props.variant === 'warning' && `background-color: ${theme.colors.babyYellow};`}
  ${props => props.variant === 'edit' && `background-color: ${theme.colors.babyYellow};`}
  ${props => props.variant === 'danger' && `background-color: ${theme.colors.babyPink};`}
`;

const styling = css<{ isTabbing: boolean }>`
  ${theme.typography.root};
  display: inline-flex;
  align-items: center;
  color: inherit;
  text-decoration: none;
  line-height: 1;
  border-bottom: 1px solid ${theme.colors.black90};
  user-select: none;
  font-size: inherit;
  outline: none;
  ${props =>
    props.isTabbing &&
    css`
      &:focus {
        outline: ${theme.colors.black} solid 2px;
        outline-offset: 2px;
      }
    `}
`;

const StyledAlertLink = styled(ReactLink)<{ isTabbing: boolean }>`
  ${styling}
`;

const StyledAlertAnchorLink = styled.a<{ isTabbing: boolean }>`
  ${styling}
`;

type AlertProps = {
  /** Changes the background color and icon of `<Alert/>` */
  variant: 'warning' | 'danger' | 'edit';
  /**  */
  size?: 'slim' | 'wide';
  /** Boolean to display icon */
  hasIcon?: boolean;
  /** Meant to be used when `<Alert/>` is to be a one-liner,
   * for both desktop and mobile, i.e. on Error alerts  */
  wrap?: boolean;
  /** A combination of margin-left and margin-right, where both share the marginal,
   * i.e: `gutterWidth={5}` then; `margin-left: 2.5px` and `margin-right:2.5px` */
  gutterWidth?: number | number[];
  /** `paddingLeft` is used in order to override `<Alert/>` padding when design doesn't follow the rules */
  paddingLeft?: number | number[];
  /** `paddingRight` is used in order to override `<Alert/>` padding when design doesn't follow the rules */
  paddingRight?: number | number[];
  /** `marginTop` is used in order to set a margin top pixel */
  marginTop?: ResponsiveSpacing;
  /** `marginBottom` is used in order to set a margin bottom pixel */
  marginBottom?: ResponsiveSpacing;
  /** Content to have inside `<Alert/>`, for example: `<Text/>` */
  children: React.ReactNode;
};

/** @deprecated Use Feedback component, which is aligned with Figma */
export const Alert = ({
  variant,
  hasIcon = true,
  size = 'slim',
  gutterWidth = 20,
  wrap = true,
  paddingLeft,
  paddingRight,
  marginTop,
  marginBottom,
  children,
}: AlertProps) => {
  const isSlim = size === 'slim';

  return (
    <StyledAlert
      paddingLeft={paddingLeft || (isSlim ? 20 : [20, 18, 63])}
      paddingRight={paddingRight || (isSlim ? 20 : [20, 58, 103])}
      paddingTop={[20, 22, isSlim ? 10 : 20]}
      paddingBottom={[wrap ? 30 : 20, 22, isSlim ? 10 : 20]}
      variant={variant}
      marginTop={marginTop}
      marginBottom={marginBottom}
    >
      <ResponsiveSpacer gutterWidth={gutterWidth} gutterHeight={5} breakpoint={wrap ? theme.breakpoint.md : 0}>
        {hasIcon && variant === 'warning' && <TriangleExclamationIcon width={20} />}
        {hasIcon && variant === 'danger' && <CircleInfoIcon width={20} />}
        {hasIcon && variant === 'edit' && <EditIcon color="black90" width={14} />}
        {children}
      </ResponsiveSpacer>
    </StyledAlert>
  );
};

type AlertLinkProps = {
  id: string;
  to: string;
  children: React.ReactNode;
};

Alert.Link = ({ id, to, children }: AlertLinkProps) => {
  const isTabbing = useTabbing();
  const urlType = getUrlType(to);

  return urlType === 'external' ? (
    <StyledAlertAnchorLink
      id={id}
      data-testid={`test-${id}`}
      href={to}
      target="_blank"
      rel="noopener no"
      isTabbing={isTabbing}
    >
      {children}
    </StyledAlertAnchorLink>
  ) : (
    <StyledAlertLink id={id} data-testid={`test-${id}`} to={to} isTabbing={isTabbing}>
      {children}
    </StyledAlertLink>
  );
};
